import { PassengerI } from '@msc-b2c/interfaces/interfaces';
import {
  ExcursionCardLanguageSource,
  PrepaidBookingExcursionLineForRefund,
  PrepaidBookingSliceForRefund,
  ShopExcursionLanguageSource,
} from '@msc-b2c/interfaces/types';
import { BookingStatusEnum, PriceBasisEnum } from '@msc-b2c/interfaces/enums';
import { hasSameExcursionStartDate } from '../../alreadypurchasedcontainer/alreadyPurchasedHelpers';

export const buildExcursionLanguageCodesForRefund = (
  isExcursionLayerOpen: boolean,
  selectedCard: ExcursionCardLanguageSource,
  shopExcursion: ShopExcursionLanguageSource
): string[] => {
  if (!isExcursionLayerOpen || !selectedCard) {
    return [];
  }
  const fromCatalog = selectedCard.activityDetails?.guideLanguageCodes ?? [];
  const fromShop =
    shopExcursion?.code === selectedCard.code ? shopExcursion.guideLanguages ?? [] : [];
  const merged = fromCatalog.length > 0 ? fromCatalog : fromShop;
  return merged.filter((code): code is string => Boolean(code));
};

const addPerCabinExcursionLangToMap = (
  map: Map<string, string>,
  ex: PrepaidBookingExcursionLineForRefund,
  passengersPerCabin: Record<string, PassengerI[]>,
  alreadyBookedPaxId: string[]
) => {
  if (!ex.cabin?.id) {
    return;
  }
  const lang = ex.preferredGuideLanguage;
  if (!lang) {
    return;
  }
  const cabinPassengers = passengersPerCabin[ex.cabin.id] ?? [];
  for (const p of cabinPassengers) {
    if (p.id && alreadyBookedPaxId.includes(p.id)) {
      map.set(`${ex.cabin.id}:${p.id}`, lang);
    }
  }
};

const addPerPersonExcursionLangToMap = (
  map: Map<string, string>,
  ex: PrepaidBookingExcursionLineForRefund,
  passengersPerCabin: Record<string, PassengerI[]>
) => {
  if (!ex.passenger?.id) {
    return;
  }
  const lang = ex.preferredGuideLanguage;
  if (!lang) {
    return;
  }
  const cabinEntry = Object.entries(passengersPerCabin).find(([, plist]) =>
    plist.some((p) => p.id === ex.passenger?.id)
  );
  const resolvedCabinId = cabinEntry?.[0];
  if (resolvedCabinId) {
    map.set(`${resolvedCabinId}:${ex.passenger.id}`, lang);
  }
};

export const buildBookedExcursionPreferredLanguageMap = (
  isExcursionLayerOpen: boolean,
  excursionCode: string | undefined,
  excursionStartDate: string | undefined,
  prepaidBookingsForCruise: PrepaidBookingSliceForRefund[],
  passengersPerCabin: Record<string, PassengerI[]>,
  alreadyBookedPaxId: string[]
): Map<string, string> => {
  const map = new Map<string, string>();
  if (!isExcursionLayerOpen || !excursionCode) {
    return map;
  }
  for (const prepaidBooking of prepaidBookingsForCruise) {
    for (const ex of prepaidBooking.excursions ?? []) {
      if (
        ex.code !== excursionCode ||
        ex.bookingStatus?.code !== BookingStatusEnum.BOOKED ||
        !hasSameExcursionStartDate(ex.startDate, excursionStartDate)
      ) {
        continue;
      }
      if (ex.priceBasis === PriceBasisEnum.PER_CABIN) {
        addPerCabinExcursionLangToMap(map, ex, passengersPerCabin, alreadyBookedPaxId);
      } else {
        addPerPersonExcursionLangToMap(map, ex, passengersPerCabin);
      }
    }
  }
  return map;
};
